import { webTransportManager } from "../entities/WebTransport";
import { ensureKeroAvailable } from "./keroService";
import { errorDetails } from "../Utils/errorDetails";

interface CaptureMetadata {
  mailboxShmName: string;

  keroPid: number;

  url: string;

  width: number;

  height: number;
}

interface GpuCaptureInstance {
  captureId: string;

  stream: MediaStream;

  metadata: CaptureMetadata;

  cleanup: () => void;

  enginePid?: number;
}

/**
 * Browser Capture Service
 *
 * Manages browser capture lifecycle using D3D11 shared GPU textures.
 *
 * Flow:
 * 1. Sends WebTransport request to Kero to create CEF capture
 * 2. Kero creates CEF browser with on_accelerated_paint → D3D11 shared textures
 * 3. SHM mailbox publishes frame metadata (NT handle, PTS, dimensions)
 * 4. A native worker bridges keyed textures to unkeyed BGRA entirely on GPU
 * 5. Preload draws GPU VideoFrames into a canvas, used directly by composition
 * 6. A generated video track supplies gallery/slot previews
 * 6. Audio: CEF outputs to OS audio, SystemAudioCapture picks it up
 */

export class BrowserCaptureService {
  private gpuCaptures: Map<string, GpuCaptureInstance> = new Map();
  private pendingCaptures = new Map<string, Promise<{ captureId: string; stream: MediaStream }>>();
  private recoveryTimer?: ReturnType<typeof setInterval>;
  private recovering = false;

  private watchEngine() {
    if (this.recoveryTimer || !window.electronAPI?.getKeroStatus) return;
    this.recoveryTimer = setInterval(() => { void this.recoverCaptures(); }, 1000);
  }

  private async recoverCaptures() {
    if (this.recovering) return;
    this.recovering = true;
    try {
      const status = await window.electronAPI?.getKeroStatus?.();
      if (status?.state !== "running" || !status.pid) return;
      for (const capture of this.gpuCaptures.values()) {
        if (capture.enginePid === status.pid) continue;
        const metadata = await this.requestKeroCapture(capture.captureId, capture.metadata.url,
          capture.metadata.width, capture.metadata.height);
        if (this.gpuCaptures.get(capture.captureId) !== capture) continue;
        await new Promise(resolve => setTimeout(resolve, 200));
        if (this.gpuCaptures.get(capture.captureId) !== capture) continue;
        const result = await window.electronAPI?.startSharedTexture?.({
          captureId: capture.captureId, ...metadata,
        });
        if (!result?.success) throw new Error(result?.error ?? "GPU recovery failed");
        capture.metadata = { ...capture.metadata, ...metadata };
        capture.enginePid = status.pid;
        console.info(`[BrowserCapture] GPU source recovered: ${capture.captureId}`);
      }
    } catch (error) {
      console.warn("[BrowserCapture] recovery failed:", errorDetails(error));
    } finally {
      this.recovering = false;
    }
  }

  /**
   * Request Kero backend to start a CEF capture and return GPU mailbox info.
   */

  private async requestKeroCapture(
    id: string,

    url: string,

    width: number,

    height: number,
  ): Promise<{
    mailboxShmName: string;

    keroPid: number;
  }> {
    await ensureKeroAvailable();
    let transport = webTransportManager.getTransport();

    if (!transport) {
      await webTransportManager.connect();

      transport = webTransportManager.getTransport();
    }

    if (!transport) {
      throw new Error(
        "WebTransport connection failed - Kero backend may not be running",
      );
    }

    const bidiStream = await transport.createBidirectionalStream();

    const writer = bidiStream.writable.getWriter();

    const reader = bidiStream.readable.getReader();

    const message = JSON.stringify({
      type: "capture_request",

      id,

      url,

      width,

      height,
    });

    await writer.write(new TextEncoder().encode(message));

    await writer.close();

    const timeoutPromise = new Promise<{ done: true; value: undefined }>(
      (resolve) =>
        setTimeout(() => resolve({ done: true, value: undefined }), 10000),
    );

    const result = await Promise.race([reader.read(), timeoutPromise]);

    if (result.done || !result.value) {
      throw new Error("No response from Kero backend");
    }

    const response = new TextDecoder().decode(result.value);

    reader.releaseLock();

    const json = JSON.parse(response);

    if (json.error) throw new Error(`Kero error: ${json.error}`);

    if (!json.mailbox_shm_name || !json.kero_pid) {
      throw new Error("Kero response missing mailbox_shm_name or kero_pid");
    }

    return {
      mailboxShmName: json.mailbox_shm_name,

      keroPid: json.kero_pid,
    };
  }

  /**
   * Create a browser capture from Kero using GPU shared textures.
   *
   * The worker and preload draw into #browser-capture-<captureId>.
   * Composition reads this GPU canvas directly; a generated track supplies previews.
   * The native bridge uses an unkeyed GPU pool supported by Electron.
   */

  async createCapture(id: string, url: string, width: number, height: number, preview = true) {
    const pending = this.pendingCaptures.get(id);
    if (pending) return pending;
    const existing = this.gpuCaptures.get(id);
    if (existing?.stream.active && existing.metadata.url === url) {
      return { captureId: id, stream: existing.stream };
    }
    const creation = this.createNewCapture(id, url, width, height, preview);
    this.pendingCaptures.set(id, creation);
    try { return await creation; }
    finally { if (this.pendingCaptures.get(id) === creation) this.pendingCaptures.delete(id); }
  }

  private async createNewCapture(
    id: string,

    url: string,

    width: number,

    height: number,
    preview: boolean,
  ): Promise<{ captureId: string; stream: MediaStream }> {
    const { mailboxShmName, keroPid } = await this.requestKeroCapture(
      id,

      url,

      width,

      height,
    ).catch(async (error: unknown) => {
      console.error("[BrowserCapture] Kero request failed:", errorDetails(error));
      // Kero can exit during the handshake or a request on a cached transport.
      await ensureKeroAvailable();
      throw error;
    });

    // Wait for Kero to create shared memory

    await new Promise((resolve) => setTimeout(resolve, 200));

    const electronAPI = window.electronAPI;

    if (!electronAPI?.startSharedTexture) {
      throw new Error("startSharedTexture not available in electronAPI");
    }

    // Create canvas for captureStream inside #area (same as other sources)

    const canvas = document.createElement("canvas");

    canvas.width = width;

    canvas.height = height;

    canvas.style.display = "none";

    // The slot video already uses id; keep the capture canvas distinct.
    canvas.id = `browser-capture-${id}`;
    canvas.setAttribute("data-browser-preview", preview ? "on" : "off");

    const container = document.querySelector("#area");

    if (!container) {
      const wrapper = document.createElement("div");

      wrapper.id = "area";

      document.body.appendChild(wrapper);

      wrapper.appendChild(canvas);
    } else {
      container.appendChild(canvas);
    }

    const ctx = canvas.getContext("2d", { alpha: true });

    if (!ctx) {
      throw new Error("Failed to get 2d context on renderer canvas");
    }

    ctx.clearRect(0, 0, width, height);

    // Preload owns the generated track and its GPU-backed VideoFrames.
    // Share its MediaStream through a temporary DOM element across isolated worlds.
    const Generator = (window as unknown as {
      MediaStreamTrackGenerator?: new (options: { kind: "video" }) =>
        MediaStreamTrack & { writable: WritableStream<VideoFrame> };
    }).MediaStreamTrackGenerator;
    let stream: MediaStream;
    if (Generator) {
      const bridgeVideo = document.createElement("video");
      bridgeVideo.id = `browser-track-${id}`;
      bridgeVideo.style.display = "none";
      canvas.after(bridgeVideo);
      const channel = new MessageChannel();
      try {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            channel.port1.close();
            reject(new Error("Browser GPU track receiver unavailable"));
          }, 5000);
          channel.port1.onmessage = (event) => {
            clearTimeout(timeout);
            channel.port1.close();
            if (event.data?.success) resolve();
            else reject(new Error(event.data?.error ?? "Browser GPU track creation failed"));
          };
          // DOM srcObject shares the native MediaStream between isolated worlds.
          // Transferring a WritableStream would serialize every imported frame.
          window.postMessage({ type: "browser-capture-track", captureId: id },
            window.location.origin, [channel.port2]);
        });
        stream = bridgeVideo.srcObject as MediaStream;
        if (!stream) throw new Error("Browser GPU track missing");
      } catch (error) {
        canvas.remove();
        throw error;
      } finally {
        bridgeVideo.remove();
      }
    } else {
      stream = canvas.captureStream(60);
    }

    // Native worker → shared GPU texture → preload canvas → composition.
    // No pixel readback or RGBA buffers cross the process boundary.
    const startRes = await electronAPI.startSharedTexture({
      captureId: id,

      mailboxShmName,

      keroPid,
    });

    if (!startRes?.success) {
      stream.getTracks().forEach(track => track.stop());
      window.postMessage({ type: "browser-capture-track-close", captureId: id }, window.location.origin);
      canvas.remove();

      throw new Error(
        `startSharedTexture failed: ${startRes?.error ?? "unknown"}`,
      );
    }

    const cleanup = () => {
      void window.electronAPI?.stopSharedTexture?.(id);

      window.postMessage({ type: "browser-capture-track-close", captureId: id }, window.location.origin);
      canvas.remove();
    };

    const enginePid = (await electronAPI.getKeroStatus?.())?.pid ?? undefined;
    const metadata: CaptureMetadata = {
      mailboxShmName,

      keroPid,

      url,

      width,

      height,
    };

    this.gpuCaptures.set(id, {
      captureId: id,

      stream,

      metadata,

      cleanup,
      enginePid,
    });
    this.watchEngine();

    return { captureId: id, stream };
  }

  /**
   * Get capture metadata.
   */

  getCaptureInfo(captureId: string): CaptureMetadata | undefined {
    return this.gpuCaptures.get(captureId)?.metadata;
  }

  /**
   * Close a browser capture (GPU teardown + Kero notification).
   */

  async close(captureId: string): Promise<void> {
    const gpu = this.gpuCaptures.get(captureId);

    if (gpu) {
      gpu.cleanup(); // stopSharedTexture (detiene el send loop del main) + remueve el canvas

      gpu.stream.getTracks().forEach((t) => t.stop());

      this.gpuCaptures.delete(captureId);
      if (this.gpuCaptures.size === 0) {
        clearInterval(this.recoveryTimer);
        this.recoveryTimer = undefined;
      }
    }

    // Notify Kero

    const transport = webTransportManager.getTransport();

    if (transport) {
      try {
        const bidiStream = await transport.createBidirectionalStream();

        const writer = bidiStream.writable.getWriter();

        await writer.write(
          new TextEncoder().encode(
            JSON.stringify({ type: "capture_close", id: captureId }),
          ),
        );

        await writer.close();
      } catch {
        // Kero close notification failed — non-critical
      }
    }
  }

  /**
   * Singleton
   */

  private static instance: BrowserCaptureService | null = null;

  static getInstance(): BrowserCaptureService {
    if (!BrowserCaptureService.instance) {
      BrowserCaptureService.instance = new BrowserCaptureService();
    }

    return BrowserCaptureService.instance;
  }
}

export const browserCaptureService = BrowserCaptureService.getInstance();
